/** biome-ignore-all lint/suspicious/noExplicitAny: GeoService location shapes / legacy shared components */
/** biome-ignore-all lint/correctness/useExhaustiveDependencies: legacy dep lists preserved */
/** biome-ignore-all lint/suspicious/noEmptyBlockStatements: legacy noop prop defaults preserved */
import { Button, Flex, Heading } from '@rocket/ui';
import { useCallback, useEffect, useState } from 'react';

import { BBAutocomplete, BBOldSelect, DualPriorityList, Location } from '../../components';
import { debounce } from '../../utils/debounce';
import geoService from '../../utils/GeoService';

type AnyData = any;

const GEO_TYPE_OPTIONS: AnyData = geoService.getGeoOptions('governing-law');
const DEBOUNCE_ASYNC_TIME = 250;

interface GeoSelectorProps {
  includeLocations?: Array<AnyData>;
  onIncludeLocationsChanged?: (locations: Array<AnyData>) => void;
  excludeLocations?: Array<AnyData>;
  onExcludeLocationsChanged?: (locations: Array<AnyData>) => void;
}

function GeoSelector({
  includeLocations = [],
  onIncludeLocationsChanged = () => {},
  excludeLocations = [],
  onExcludeLocationsChanged = () => {},
}: GeoSelectorProps) {
  const [jurisdictionType, setJurisdictionType] = useState('');
  const [locationSearchValue, setLocationSearchValue] = useState('');
  const [locationSearchMode, setLocationSearchMode] = useState<string | undefined>();
  const [locationValue, setLocationValue] = useState<AnyData>(null);
  const [locationSearchOptions, setLocationSearchOptions] = useState<Array<AnyData>>([]);
  const [locationSearchOptionsLoading, setLocationSearchOptionsLoading] = useState(false);

  useEffect(() => {
    const isComponentActive = { active: true };
    if (jurisdictionType) {
      const { searchMode } = GEO_TYPE_OPTIONS[jurisdictionType];
      if (locationSearchValue.length && searchMode === 'remote') {
        setLocationSearchOptionsLoading(true);
        getSearchOptionsDelayed(locationSearchValue, jurisdictionType, isComponentActive);
      }
    }
    return () => {
      isComponentActive.active = false;
    };
  }, [locationSearchValue]);

  useEffect(() => {
    const isComponentActive = { active: true };
    setLocationSearchOptions([]);
    if (jurisdictionType) {
      const { searchMode } = GEO_TYPE_OPTIONS[jurisdictionType];
      setLocationSearchMode(searchMode);
      fetchSearchResults('', jurisdictionType, isComponentActive);
    }
    return () => {
      isComponentActive.active = false;
    };
  }, [jurisdictionType]);

  const getSearchOptionsDelayed = useCallback(
    debounce((searchTerms: string, jType: string, isComponentActive: AnyData) => {
      fetchSearchResults(searchTerms, jType, isComponentActive);
    }, DEBOUNCE_ASYNC_TIME),
    []
  );

  function handleAddInclude() {
    if (includeLocations.map((loc) => loc.id).includes(locationValue.id)) {
      return;
    }
    onIncludeLocationsChanged([...includeLocations, locationValue]);
    setLocationValue(null);
  }

  function handleAddExclude() {
    if (excludeLocations.map((loc) => loc.id).includes(locationValue.id)) {
      return;
    }
    onExcludeLocationsChanged([...excludeLocations, locationValue]);
    setLocationValue(null);
  }

  async function fetchSearchResults(
    searchTerms: string,
    jType: string,
    isComponentActive: AnyData
  ) {
    const jurisdictionFilters = GEO_TYPE_OPTIONS[jType]?.filters;
    if (!isComponentActive.active || searchTerms == null || !jType) {
      setLocationSearchOptionsLoading(false);
      return Promise.resolve();
    }
    setLocationSearchOptionsLoading(true);
    const searchResults = await geoService.fetchGeoData({
      tokens: searchTerms.split(' ').map((t) => t.trim()),
      filters: jurisdictionFilters,
    });
    const searchResultsAnnotated = searchResults.map((sr: AnyData) => ({
      ...sr,
      category: jType,
    }));
    setLocationSearchOptions(searchResultsAnnotated);
    setLocationSearchOptionsLoading(false);
    return searchResults;
  }

  const alreadyPicked =
    !!locationValue &&
    (includeLocations.map((loc) => loc.id).includes(locationValue.id) ||
      excludeLocations.map((loc) => loc.id).includes(locationValue.id));

  return (
    <Flex.H gap="16px" alignItems="flex-start">
      <Flex.V gap="16px" width="33%" marginTop="8px">
        <BBOldSelect
          label="Jurisdiction Type"
          value={jurisdictionType}
          options={Object.keys(GEO_TYPE_OPTIONS)}
          onChange={(evt) => setJurisdictionType(evt.target.value)}
        />
        <BBAutocomplete
          label="Search Locations"
          options={locationSearchOptions}
          value={locationValue}
          onChange={(val: AnyData) => setLocationValue(val)}
          disabled={!jurisdictionType}
          loading={locationSearchOptionsLoading}
          // remote jurisdictions search the server per keystroke (debounced above);
          // local ones filter the preloaded list inside the Combobox
          onSearchChange={
            locationSearchMode === 'remote'
              ? (text: string) => setLocationSearchValue(text)
              : undefined
          }
        />
        <Button width="100%" disabled={!locationValue || alreadyPicked} onClick={handleAddInclude}>
          Accept this Jurisdiction
        </Button>
        <Heading as="h4" textAlign="center">
          OR
        </Heading>
        <Button
          colorPalette="red"
          width="100%"
          disabled={!locationValue || alreadyPicked}
          onClick={handleAddExclude}
        >
          Exclude this Jurisdiction
        </Button>
      </Flex.V>
      <Flex.V width="67%">
        <DualPriorityList
          includeItems={includeLocations}
          onIncludeItemsChanged={(locs: Array<AnyData>) => onIncludeLocationsChanged(locs)}
          excludeItems={excludeLocations}
          onExcludeItemsChanged={(locs: Array<AnyData>) => onExcludeLocationsChanged(locs)}
          includeTitle="Accept Jurisdictions"
          excludeTitle="Exclude Jurisdictions"
          getLabel={(loc: AnyData) => <Location location={loc} />}
        />
      </Flex.V>
    </Flex.H>
  );
}

export default GeoSelector;
